// src/permissions/definition.js
// Centralized list of actions and default role grants for the permission system.
// This serves as the client-side source of truth and seeds the Firestore doc
// when none exists. Keep action ids stable; they are referenced across the app
// and in Firestore under appConfig/permissions.

export const ACTIONS = Object.freeze({
  NAV_VIEW_PROJECTS: "nav.viewProjects",
  NAV_VIEW_TASKS: "nav.viewTasks",
  NAV_VIEW_CLIENTS: "nav.viewClients",
  NAV_VIEW_ANALYTICS: "nav.viewAnalytics",
  NAV_VIEW_SCHEDULE: "nav.viewSchedule",
  PROJECT_VIEW_DETAIL: "project.viewDetail",
  FINANCIALS_VIEW: "financials.view",
  PROJECTS_CREATE: "projects.create",
  PROJECTS_EDIT: "projects.edit",
  PROJECTS_DELETE: "projects.delete",
  CLIENTS_EDIT: "clients.edit",
  INVOICES_CREATE: "invoices.create",
  PROJECTS_EXPORT: "projects.export",
  TASKS_FILTER_ALL_ANALYSTS: "tasks.filterAllAnalysts",
  SCHEDULE_MANAGE_ALLOCATIONS: "schedule.manageAllocations",
  SETTINGS_MANAGE_PERMISSIONS: "settings.managePermissions",
});

export const DEFAULT_ROLES = Object.freeze({
  admin: [
    ACTIONS.NAV_VIEW_PROJECTS,
    ACTIONS.NAV_VIEW_TASKS,
    ACTIONS.NAV_VIEW_CLIENTS,
    ACTIONS.NAV_VIEW_ANALYTICS,
    ACTIONS.NAV_VIEW_SCHEDULE,
    ACTIONS.PROJECT_VIEW_DETAIL,
    ACTIONS.FINANCIALS_VIEW,
    ACTIONS.PROJECTS_CREATE,
    ACTIONS.PROJECTS_EDIT,
    ACTIONS.PROJECTS_DELETE,
    ACTIONS.CLIENTS_EDIT,
    ACTIONS.INVOICES_CREATE,
    ACTIONS.PROJECTS_EXPORT,
    ACTIONS.TASKS_FILTER_ALL_ANALYSTS,
    ACTIONS.SCHEDULE_MANAGE_ALLOCATIONS,
    ACTIONS.SETTINGS_MANAGE_PERMISSIONS,
  ],
  finance: [
    ACTIONS.NAV_VIEW_PROJECTS,
    ACTIONS.NAV_VIEW_TASKS,
    ACTIONS.NAV_VIEW_CLIENTS,
    ACTIONS.NAV_VIEW_ANALYTICS,
    ACTIONS.NAV_VIEW_SCHEDULE,
    ACTIONS.PROJECT_VIEW_DETAIL,
    ACTIONS.FINANCIALS_VIEW,
    ACTIONS.PROJECTS_CREATE,
    ACTIONS.PROJECTS_EDIT,
    ACTIONS.CLIENTS_EDIT,
    ACTIONS.INVOICES_CREATE,
    ACTIONS.PROJECTS_EXPORT,
    ACTIONS.TASKS_FILTER_ALL_ANALYSTS,
    ACTIONS.SCHEDULE_MANAGE_ALLOCATIONS,
  ],
  manager: [
    ACTIONS.NAV_VIEW_PROJECTS,
    ACTIONS.NAV_VIEW_TASKS,
    ACTIONS.NAV_VIEW_CLIENTS,
    ACTIONS.NAV_VIEW_ANALYTICS,
    ACTIONS.NAV_VIEW_SCHEDULE,
    ACTIONS.PROJECT_VIEW_DETAIL,
    ACTIONS.PROJECTS_CREATE,
    ACTIONS.PROJECTS_EDIT,
    ACTIONS.CLIENTS_EDIT,
    ACTIONS.INVOICES_CREATE,
    ACTIONS.PROJECTS_EXPORT,
    ACTIONS.SCHEDULE_MANAGE_ALLOCATIONS,
  ],
  analyst: [
    ACTIONS.NAV_VIEW_TASKS,
    ACTIONS.NAV_VIEW_SCHEDULE,
    ACTIONS.TASKS_FILTER_ALL_ANALYSTS,
  ],
  restricted: [
    ACTIONS.NAV_VIEW_TASKS,
  ],
});

export const DEFAULT_PERMISSION_CONFIG = Object.freeze({
  defaultRole: "restricted",
  roles: DEFAULT_ROLES,
  users: {}, // email -> { roles: string[], overrides: { [actionId]: true|false } }
});

export const PERMISSIONS_DOC_PATH = { collection: "appConfig", doc: "permissions" };

// Utility to list all action ids as an array (useful for UI forms and admin screens).
export const ALL_ACTIONS = Object.values(ACTIONS);
